<script lang="ts">
    import { onDestroy } from 'svelte';

    export let isOpen = false;
    export let iframeUrl = '';
    export let onClose: () => void = () => null;

    let backdropElement: HTMLDivElement;
    let iframeContainer: HTMLDivElement;

    const closePopup = () => {
        isOpen = false;
        onClose();
    };

    const handleBackdropClick = (event: MouseEvent) => {
        if (event.target === backdropElement) {
            closePopup();
        }
    };

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            closePopup();
        }
    };

    // Block body scroll when popup is open
    // for reference - Calendly does the same thing with popup embedding
    $: if (isOpen) {
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeydown);
    } else {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeydown);
    }

    onDestroy(() => {
        window.removeEventListener('keydown', handleKeydown);
        document.body.style.overflow = '';
    });

    $: if (isOpen && iframeContainer && iframeUrl) {
        // eslint-disable-next-line svelte/no-dom-manipulating
        iframeContainer.innerHTML = '';

        const iframe = document.createElement('iframe');
        iframe.src = iframeUrl;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.style.display = 'block';
        iframe.id = 'calendar_iframe_' + Date.now();
        // Accessibility attributes for the iframe
        iframe.title = 'Calendar booking widget';
        iframe.setAttribute('aria-label', 'Calendar booking widget');

        // eslint-disable-next-line svelte/no-dom-manipulating
        iframeContainer.appendChild(iframe);
    }
</script>

{#if isOpen}
    <!-- Backdrop -->
    <div
        class="fun-fixed fun-inset-0 fun-z-50 fun-bg-black/60"
        bind:this={backdropElement}
        on:click={handleBackdropClick}
        on:keydown={handleKeydown}
        role="button"
        tabindex="-1"
        aria-label="Close calendar booking"
    ></div>

    <!-- Dialog container -->
    <div
        class="fun-pointer-events-none fun-fixed fun-inset-0 fun-z-50 fun-flex fun-h-full fun-w-full fun-items-center fun-justify-center"
        role="dialog"
        aria-modal="true"
        aria-label="Calendar booking"
    >
        <button
            class="fun-pointer-events-auto fun-fixed fun-right-4 fun-top-4 fun-flex fun-size-10 fun-items-center fun-justify-center fun-p-0 fun-text-4xl fun-font-bold fun-text-white"
            on:click={closePopup}
            aria-label="Close calendar"
        >
            &times;
        </button>

        <div
            class="xs:fun-pt-0 fun-pointer-events-auto fun-flex fun-h-screen fun-max-h-full fun-min-h-[auto] fun-w-full fun-min-w-[auto] fun-max-w-full fun-items-center fun-justify-center fun-pt-16 sm:fun-mx-8 sm:fun-h-full sm:fun-max-h-[90vh] lg:fun-min-h-[40rem] lg:fun-min-w-[62.5rem] lg:fun-max-w-[50vw]"
        >
            <div
                class="fun-h-full fun-w-full fun-overflow-hidden"
                bind:this={iframeContainer}
                role="region"
                aria-label="Calendar widget"
            ></div>
        </div>
    </div>
{/if}
